import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useTonAddress, useTonWallet } from '@tonconnect/ui-react';
import Layout from './components/Layout';
import OnboardingFlow, { ONBOARDING_KEY } from './components/onboarding/OnboardingFlow';
import ToastContainer from './components/ToastContainer';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { TelegramAuthProvider } from './providers/TelegramAuthProvider';
import { useTelegramApp } from './hooks/useTelegramApp';
import { setLanguageFromTelegram } from './i18n';
import { useWalletStore } from './store/wallet';
import { startPriceFeed, stopPriceFeed } from './services/priceFeed';

const HomePage = lazy(() => import('./pages/HomePage'));
const MarketsPage = lazy(() => import('./pages/MarketsPage'));
const MarketDetailPage = lazy(() => import('./pages/MarketDetailPage'));
const EarnPage = lazy(() => import('./pages/EarnPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const ForecastPage = lazy(() => import('./pages/ForecastPage'));
const SquadsPage = lazy(() => import('./pages/SquadsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage'));
const CreateAgentPage = lazy(() => import('./pages/CreateAgentPage'));
const RulesPage = lazy(() => import('./pages/RulesPage'));
const PerpTradePage = lazy(() => import('./pages/perps/PerpTradePage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const MarketWizard = lazy(() => import('./pages/admin/MarketWizard'));
const MapPage = lazy(() => import('./pages/MapPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const WalletDashboard = lazy(() => import('./pages/wallet/WalletDashboard'));
const WalletSend = lazy(() => import('./pages/wallet/WalletSend'));
const WalletShield = lazy(() => import('./pages/wallet/WalletShield'));
const WalletHistory = lazy(() => import('./pages/wallet/WalletHistory'));
const CellDashboard = lazy(() => import('./pages/CellDashboard'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));


/**
 * On app.whether.market, the root "/" redirects to /perps (MarketsPage).
 * On www.whether.market / whether.market, the root "/" shows the landing page.
 */
function RootRoute() {
    const isAppSubdomain = window.location.hostname === 'app.whether.market';
    if (isAppSubdomain) {
        return <Navigate to="/perps" replace />;
    }
    return <HomePage />;
}

function App() {
    const { webApp, ready, user } = useTelegramApp();
    const tonAddress = useTonAddress();
    const tonWallet = useTonWallet();
    const { setAddress, walletType, address: storeAddress, disconnect: storeDisconnect } = useWalletStore();
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [forceOnboarding, setForceOnboarding] = useState(false);

    useEffect(() => {
        if (webApp && ready) {
            webApp.expand();
            webApp.enableClosingConfirmation();
            // Set language from Telegram user preferences
            setLanguageFromTelegram(user?.language_code);
        }
    }, [webApp, ready, user?.language_code]);

    // Expose wallet address globally so perpStore can access it for points recording
    useEffect(() => {
        (window as any).__tonWalletAddress = tonAddress || null;
    }, [tonAddress]);

    // Sync TonConnect wallet state into the enhanced wallet store
    useEffect(() => {
        if (tonWallet && tonAddress && walletType !== 'walletconnect') {
            if (storeAddress !== tonAddress) {
                setAddress(tonAddress, 'tonconnect');
            }
        } else if (!tonWallet && walletType === 'tonconnect') {
            storeDisconnect();
        }
    }, [tonWallet, tonAddress, walletType, storeAddress, setAddress, storeDisconnect]);

        // Start energy oracle price feed polling
    useEffect(() => {
        startPriceFeed();
        return () => stopPriceFeed();
    }, []);

    // Show onboarding on first visit
    useEffect(() => {
        const completed = localStorage.getItem(ONBOARDING_KEY);
        if (!completed) {
            setShowOnboarding(true);
        }
    }, []);

    return (
        <TelegramAuthProvider>
            <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/" element={<RootRoute />} />
                        <Route path="/forecast" element={<ForecastPage />} />
                        <Route path="/markets" element={<MarketsPage />} />
                        <Route path="/markets/:marketId" element={<MarketDetailPage />} />
                        <Route path="/earn" element={<EarnPage />} />
                        <Route path="/portfolio" element={<PortfolioPage />} />
                        <Route path="/squads" element={<SquadsPage />} />
                        <Route path="/squads/:squadId" element={<SquadsPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/achievements" element={<AchievementsPage />} />
                        <Route path="/leaderboard" element={<LeaderboardPage />} />
                        <Route path="/agents" element={<AgentsPage />} />
                        <Route path="/agents/create" element={<CreateAgentPage />} />
                        <Route path="/agents/:agentId" element={<AgentDetailPage />} />
                        <Route path="/rules" element={<RulesPage />} />
                        <Route path="/perps" element={<MarketsPage />} />
                        <Route path="/perps/:marketId" element={<PerpTradePage />} />
                        <Route path="/map" element={<MapPage />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/referrals" element={<ReferralPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/journal" element={<JournalPage />} />
                        <Route path="/wallet" element={<WalletDashboard />} />
                        <Route path="/wallet/send" element={<WalletSend />} />
                        <Route path="/wallet/shield" element={<WalletShield />} />
                        <Route path="/wallet/history" element={<WalletHistory />} />
                        <Route path="/cells" element={<CellDashboard />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/create" element={<MarketWizard />} />
                        <Route path="/docs" element={<DocsPage />} />
                        <Route path="/status" element={<StatusPage />} />
                        <Route path="/legal" element={<LegalPage />} />
                        <Route path="/legal/:docId" element={<LegalPage />} />
                    </Routes>
                </Suspense>
                {showOnboarding && (
                    <OnboardingFlow
                        forceShow={forceOnboarding}
                        onComplete={() => { setShowOnboarding(false); setForceOnboarding(false); }}
                    />
                )}
            </Layout>
        <ToastContainer />
        </TelegramAuthProvider>
    );
}

export default App;
